import React, { useState } from 'react'
import { BrandLogo, BrandStandards } from '@marketing-milk/interfaces'
import { SurfacePalette } from '../../tokens'
import { useUpdateSprintPacket } from 'hooks/api/useSprintPackets'
import { SectionCard } from './SectionCard'
import { BrandStandardsContent } from './BrandStandardsContent'
import { BrandStandardsEditor } from './BrandStandardsEditor'

interface Props {
  palette: SurfacePalette
  packetID: number
  brandStandards: BrandStandards | null
  brandLogos: BrandLogo[]
}

/**
 * Edit-mode Brand Standards section (v1.6). Packet-owned display data — logos,
 * fonts, colors — hand-entered here, copied forward from the previous packet on
 * creation, rendered read-only in Present. Persists through the shared packet
 * update path; the section keeps a packet-local `section` comment anchor.
 */
export const BrandStandardsSection = ({ palette, packetID, brandStandards, brandLogos }: Props) => {
  const [editing, setEditing] = useState(false)
  const updatePacket = useUpdateSprintPacket(packetID)

  const onSave = (next: BrandStandards) =>
    updatePacket.mutate({ brandStandards: next }, { onSuccess: () => setEditing(false) })

  return (
    <SectionCard
      palette={palette}
      sectionKey="brand-standards"
      title="Brand Standards"
      commentAnchor={{
        type: 'section',
        id: `${packetID}:brand-standards`,
        label: 'Brand Standards',
      }}
    >
      <BrandStandardsContent
        palette={palette}
        brandLogos={brandLogos}
        fonts={brandStandards?.fonts ?? []}
        colors={brandStandards?.colors ?? []}
        editable
        onEdit={() => setEditing(true)}
      />
      {editing && (
        <BrandStandardsEditor
          palette={palette}
          brandStandards={brandStandards}
          brandLogos={brandLogos}
          saving={updatePacket.isLoading}
          onSave={onSave}
          onClose={() => setEditing(false)}
        />
      )}
    </SectionCard>
  )
}
